import { ref, watch } from 'vue';
import useUserStore from '@/stores/useUsersStore';
import useViewAsStore from '@/stores/useViewAsStore';
import { ACL_PAGE_PERMISSIONS } from '@/modules/acl/aclPermissions';
import { isViewAsAllowed } from '@/modules/acl/viewAsRole';
import {
  CurrentUserApiResource,
  CurrentUserApiResourceType,
  GuestApiResource,
  ManagerApiResource,
  OwnerApiResource,
  VendorApiResource,
} from '@/api';

enum EUserRole {
  ROLE_ADMIN = 'admin',
  ROLE_MANAGER = 'manager',
  ROLE_GUEST = 'guest',
  ROLE_OWNER = 'owner',
  ROLE_VENDOR = 'vendor',
  ROLE_ANONYMOUS = 'anonymous',
}
export type TUserRole = EUserRole.ROLE_ADMIN | EUserRole.ROLE_MANAGER | EUserRole.ROLE_GUEST | EUserRole.ROLE_OWNER | EUserRole.ROLE_ANONYMOUS | EUserRole.ROLE_VENDOR;

export type Permission = string;

export type TAclRule = Record<TUserRole, Permission[]>;

const getUserRole = (user: CurrentUserApiResource | ManagerApiResource | GuestApiResource | OwnerApiResource | VendorApiResource | null): TUserRole => {
  if (user?.type === CurrentUserApiResourceType.admin) {
    return EUserRole.ROLE_ADMIN;
  }

  if (user?.type === CurrentUserApiResourceType.manager) {
    return EUserRole.ROLE_MANAGER;
  }

  if (user?.type === CurrentUserApiResourceType.guest) {
    return EUserRole.ROLE_GUEST;
  }

  if (user?.type === CurrentUserApiResourceType.owner) {
    return EUserRole.ROLE_OWNER;
  }

  if (user?.type === CurrentUserApiResourceType.vendor) {
    return EUserRole.ROLE_VENDOR;
  }

  return EUserRole.ROLE_ANONYMOUS;
};

export class AclService {
  private currentRole = ref<TUserRole>(EUserRole.ROLE_ANONYMOUS);
  private rules: TAclRule = {
    admin: [
      ACL_PAGE_PERMISSIONS.PAGE_MANAGERS,
      ACL_PAGE_PERMISSIONS.PAGE_OWNERS,
      ACL_PAGE_PERMISSIONS.PAGE_GUESTS,
      ACL_PAGE_PERMISSIONS.PAGE_VENDORS,
      ACL_PAGE_PERMISSIONS.PAGE_PAYMENTS,
      ACL_PAGE_PERMISSIONS.PAGE_LOCATIONS,
      ACL_PAGE_PERMISSIONS.PAGE_DEPARTMENTS,
      ACL_PAGE_PERMISSIONS.PAGE_EMAIL_TEMPLATES,
      ACL_PAGE_PERMISSIONS.PAGE_SMS_TEMPLATES,
      ACL_PAGE_PERMISSIONS.PAGE_FEATURE_FLAGS,
    ],
    manager: [ACL_PAGE_PERMISSIONS.PAGE_PROPERTY_OWNERS, ACL_PAGE_PERMISSIONS.PAGE_PROPERTY_VENDORS],
    owner: [],
    vendor: [],
    guest: [],
    anonymous: [ACL_PAGE_PERMISSIONS.PAGE_LOGIN],
  };

  private userInit = false;

  private initUserWatch(): void {
    if (this.userInit) {
      return;
    }

    const userStore = useUserStore();
    const viewAsStore = useViewAsStore();

    const computeRole = (): void => {
      const user = userStore.user;

      if (!user) {
        acl.setRole(EUserRole.ROLE_ANONYMOUS);

        return;
      }

      if (isViewAsAllowed(user, viewAsStore.viewAsRole)) {
        acl.setRole(viewAsStore.viewAsRole as TUserRole);
      } else {
        acl.setRole(getUserRole(user));
      }
    };

    watch(() => userStore.user, computeRole, { immediate: true });
    watch(() => viewAsStore.viewAsRole, computeRole);
    this.userInit = true;
  }

  setRole(role: TUserRole): void {
    this.currentRole.value = role;
  }

  addPermission(permission: Permission, roles: TUserRole[]): void {
    for (const role of roles) {
      if (!this.rules[role]) {
        this.rules[role] = [];
      }

      if (!this.rules[role].includes(permission)) {
        this.rules[role].push(permission);
      }
    }
  }

  hasPermission(permission: Permission): boolean {
    this.initUserWatch();
    const userPermissions = this.rules[this.currentRole.value] || [];

    return userPermissions.some((userPerm) => {
      if (userPerm === '*') {
        return true;
      }

      if (userPerm === permission) {
        return true;
      }

      const [userBase] = userPerm.split('.*');
      const [permBase] = permission.split('.');

      return userBase === permBase && userPerm.endsWith('.*');
    });
  }
}

export const acl = new AclService();
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_DASHBOARD, [EUserRole.ROLE_MANAGER, EUserRole.ROLE_OWNER]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_INCOME, []);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_BOOKINGS, [EUserRole.ROLE_MANAGER, EUserRole.ROLE_OWNER]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_BOOKING_CALENDAR, [EUserRole.ROLE_MANAGER]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_PAYMENTS, [EUserRole.ROLE_ADMIN, EUserRole.ROLE_MANAGER, EUserRole.ROLE_OWNER, EUserRole.ROLE_GUEST]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_INVOICES, [EUserRole.ROLE_MANAGER, EUserRole.ROLE_OWNER, EUserRole.ROLE_VENDOR]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_BANK_ACCOUNTS, [EUserRole.ROLE_OWNER, EUserRole.ROLE_VENDOR]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_NOTIFICATIONS, [EUserRole.ROLE_ADMIN, EUserRole.ROLE_MANAGER, EUserRole.ROLE_OWNER]);
acl.addPermission(ACL_PAGE_PERMISSIONS.PAGE_VENDOR_PROPERTIES, [EUserRole.ROLE_VENDOR]);
